// T040 — Barrel export for all event definitions + typed onEvent function.
// onEvent accepts ONLY defined event names and enforces the correct property
// shape for each via mapped types.

import { trackEvent } from '@/lib/analytics';
import { useImpersonationStore } from '@/lib/impersonation/store';
import { reportFunnelEvent } from '@/lib/telemetry/funnel-beacon';
import { scrubGrantTokens } from '@/lib/telemetry/scrub';
import {
  APP_UPSELL_CLICKED,
  APP_UPSELL_DISMISSED,
  APP_UPSELL_VIEWED,
  type AppUpsellClickedProperties,
  type AppUpsellProperties,
} from './app-upsells';
import type {
  AccountDeletedProperties,
  AuthCallbackHangProperties,
  AuthErrorProperties,
  AuthMethodUsedProperties,
  CurrencyChangedProperties,
  LocaleChangedProperties,
  LocationChangedProperties,
  LogoutErrorProperties,
  LogoutProperties,
  ProfileUpdatedProperties,
  ProfileViewedProperties,
  SessionRefreshProperties,
  SessionRestoredProperties,
  SessionRestoreErrorProperties,
  TurnkeyPopupCallbackSkipProperties,
  TurnkeyRefreshFailedProperties,
  TurnkeySessionExpiredProperties,
  TurnkeySessionReconcileLogoutProperties,
  TurnkeySessionRefreshedProperties,
  WalletConnectedProperties,
  WalletCreatedProperties,
  WalletExportProperties,
  WalletReconcileSkippedForeignSubOrgProperties,
  WalletRenamedProperties,
  WalletSwitchedProperties,
  WalletUnlinkedProperties,
  WalletUnreachableSubOrgProperties,
} from './auth';
import {
  ACCOUNT_DELETED,
  AUTH_CALLBACK_HANG,
  AUTH_ERROR,
  AUTH_METHOD_USED,
  CURRENCY_CHANGED,
  LOCALE_CHANGED,
  LOCATION_CHANGED,
  LOGOUT,
  LOGOUT_ERROR,
  PROFILE_UPDATED,
  PROFILE_VIEWED,
  SESSION_REFRESH,
  SESSION_RESTORE_ERROR,
  SESSION_RESTORED,
  TURNKEY_POPUP_CALLBACK_SKIP,
  TURNKEY_REFRESH_FAILED,
  TURNKEY_SESSION_EXPIRED,
  TURNKEY_SESSION_RECONCILE_LOGOUT,
  TURNKEY_SESSION_REFRESHED,
  WALLET_CONNECTED,
  WALLET_CREATED,
  WALLET_EXPORT,
  WALLET_RENAMED,
  WALLET_SWITCHED,
  WALLET_UNLINKED,
} from './auth';
import type { BookmarkToggledProperties } from './bookmark-toggled';
import { BOOKMARK_TOGGLED } from './bookmark-toggled';
import type { BoostCompletedProperties } from './boost-completed';
import { BOOST_COMPLETED } from './boost-completed';
import type { BoostInitiatedProperties } from './boost-initiated';
import { BOOST_INITIATED } from './boost-initiated';
import type { ClaimConfirmedProperties } from './claim-confirmed';
import type { ClaimInitiatedProperties } from './claim-initiated';
import { CLAIM_INITIATED } from './claim-initiated';
import type {
  EarlyAccessWaitlistOpenedProperties,
  WaitlistOtpFailedProperties,
  WaitlistOtpSentProperties,
  WaitlistOtpVerifiedProperties,
  WaitlistXJoinedProperties,
  WaitlistXStartedProperties,
} from './early-access';
import {
  EARLY_ACCESS_WAITLIST_OPENED,
  WAITLIST_OTP_FAILED,
  WAITLIST_OTP_SENT,
  WAITLIST_OTP_VERIFIED,
  WAITLIST_X_JOINED,
  WAITLIST_X_STARTED,
} from './early-access';
import type {
  EngineerImpersonationStartedProperties,
  EngineerImpersonationStoppedProperties,
} from './engineer-impersonation';
import {
  ENGINEER_IMPERSONATION_STARTED,
  ENGINEER_IMPERSONATION_STOPPED,
} from './engineer-impersonation';
import type {
  FaucetClaimFailedProperties,
  FaucetClaimInitiatedProperties,
  FaucetClaimSucceededProperties,
} from './faucet';
import { FAUCET_CLAIM_FAILED, FAUCET_CLAIM_INITIATED, FAUCET_CLAIM_SUCCEEDED } from './faucet';
import type {
  FeaturedHeroBookmarkToggledProperties,
  FeaturedHeroCardClickedProperties,
  FeaturedHeroCarouselAdvancedProperties,
  FeaturedHeroShareOpenedProperties,
  FeaturedHeroThesisClickedProperties,
  FeaturedHeroViewedProperties,
} from './featured-hero';
import {
  FEATURED_HERO_BOOKMARK_TOGGLED,
  FEATURED_HERO_CARD_CLICKED,
  FEATURED_HERO_CAROUSEL_ADVANCED,
  FEATURED_HERO_SHARE_OPENED,
  FEATURED_HERO_THESIS_CLICKED,
  FEATURED_HERO_VIEWED,
} from './featured-hero';
import type { FeedbackOpenedProperties, FeedbackSubmittedProperties } from './feedback';
import { FEEDBACK_OPENED, FEEDBACK_SUBMITTED } from './feedback';
import type {
  GiftAssetSelectedProperties,
  GiftClaimCompletedProperties,
  GiftClaimInitiatedProperties,
  GiftModalDismissedProperties,
  GiftModalOpenedProperties,
  GiftOnboardingCompletedViaClaimProperties,
  GiftOutcomeTokenPickedProperties,
  GiftPermalinkViewedProperties,
  GiftSendCompletedProperties,
  GiftSendInitiatedProperties,
  GiftShareClickedCopyLinkProperties,
  GiftShareClickedProperties,
  GiftShareClickedXProperties,
  GiftWithdrawCompletedProperties,
  GiftWithdrawInitiatedProperties,
} from './gifts';
import {
  GIFT_ASSET_SELECTED,
  GIFT_CLAIM_COMPLETED,
  GIFT_CLAIM_INITIATED,
  GIFT_MODAL_DISMISSED,
  GIFT_MODAL_OPENED,
  GIFT_ONBOARDING_COMPLETED_VIA_CLAIM,
  GIFT_OUTCOME_TOKEN_PICKED,
  GIFT_PERMALINK_VIEWED,
  GIFT_SEND_COMPLETED,
  GIFT_SEND_INITIATED,
  GIFT_SHARE_CLICKED,
  GIFT_SHARE_CLICKED_COPY_LINK,
  GIFT_SHARE_CLICKED_X,
  GIFT_WITHDRAW_COMPLETED,
  GIFT_WITHDRAW_INITIATED,
} from './gifts';
import type {
  ImageGenerationCompletedProperties,
  ImageGenerationFailedProperties,
  ImageGenerationRetriedProperties,
  ImageGenerationStartedProperties,
  ImageGenerationUsedProperties,
  ImageSearchCompletedProperties,
  ImageSearchFailedProperties,
  ImageSearchStartedProperties,
  ImageSearchUsedProperties,
} from './image-generation';
import {
  IMAGE_GENERATION_COMPLETED,
  IMAGE_GENERATION_FAILED,
  IMAGE_GENERATION_RETRIED,
  IMAGE_GENERATION_STARTED,
  IMAGE_GENERATION_USED,
  IMAGE_SEARCH_COMPLETED,
  IMAGE_SEARCH_FAILED,
  IMAGE_SEARCH_STARTED,
  IMAGE_SEARCH_USED,
} from './image-generation';
import type { MarketApproachingResolutionViewedProperties } from './market-approaching-resolution-viewed';
import { MARKET_APPROACHING_RESOLUTION_VIEWED } from './market-approaching-resolution-viewed';
import type { MarketDetailViewedProperties } from './market-detail-viewed';
import { MARKET_DETAIL_VIEWED } from './market-detail-viewed';
import type { MarketLifecycleModalShownProperties } from './market-lifecycle';
import { MARKET_LIFECYCLE_MODAL_SHOWN } from './market-lifecycle';
import type { MarketResultSharedProperties } from './market-result-shared';
import { MARKET_RESULT_SHARED } from './market-result-shared';
import type { MarketResultViewedProperties } from './market-result-viewed';
import { MARKET_RESULT_VIEWED } from './market-result-viewed';
import type {
  MarketsBookmarkToggledProperties,
  MarketsCardClickedProperties,
  MarketsFilterChangedProperties,
  MarketsLoadMoreProperties,
  MarketsPageViewedProperties,
  MarketsSearchQueriedProperties,
  MarketsSortChangedProperties,
} from './markets';
import {
  MARKETS_BOOKMARK_TOGGLED,
  MARKETS_CARD_CLICKED,
  MARKETS_FILTER_CHANGED,
  MARKETS_LOAD_MORE,
  MARKETS_PAGE_VIEWED,
  MARKETS_SEARCH_QUERIED,
  MARKETS_SORT_CHANGED,
} from './markets';
import type { BreakingViewTappedProperties } from './markets-feed';
import { BREAKING_VIEW_TAPPED } from './markets-feed';
import type { PageViewedProperties } from './navigation';
import { PAGE_VIEWED } from './navigation';
import type {
  OnboardingCompletedProperties,
  OnboardingExitedProperties,
  OnboardingStageCompletedProperties,
  OnboardingStageViewedProperties,
  OnboardingStartedProperties,
} from './onboarding';
import {
  ONBOARDING_COMPLETED,
  ONBOARDING_EXITED,
  ONBOARDING_STAGE_COMPLETED,
  ONBOARDING_STAGE_VIEWED,
  ONBOARDING_STARTED,
} from './onboarding';
import type { OutcomeSelectedProperties } from './outcome-selected';
import { OUTCOME_SELECTED } from './outcome-selected';
import type { PnlCardSharedProperties } from './pnl-card-shared';
import { PNL_CARD_SHARED } from './pnl-card-shared';
import type { PnlMarketCardSharedProperties } from './pnl-market-card-shared';
import { PNL_MARKET_CARD_SHARED } from './pnl-market-card-shared';
import type {
  PortfolioPositionSellClickedProperties,
  PortfolioPositionsToggledProperties,
} from './portfolio';
import { PORTFOLIO_POSITION_SELL_CLICKED, PORTFOLIO_POSITIONS_TOGGLED } from './portfolio';
import type { PrizeClaimSharedProperties } from './prize-claim-shared';
import { PRIZE_CLAIM_SHARED } from './prize-claim-shared';
import type {
  ProfilePortfolioCardKpiClickedProperties,
  ProfilePortfolioCardShareClickedProperties,
} from './profile-portfolio-card';
import {
  PROFILE_PORTFOLIO_CARD_KPI_CLICKED,
  PROFILE_PORTFOLIO_CARD_SHARE_CLICKED,
} from './profile-portfolio-card';
import type {
  CreatorRewardsPanelViewedProperties,
  DuplicateNudgeDismissedProperties,
  DuplicateNudgeShownProperties,
  ProposalAdminApprovedProperties,
  ProposalAdminRejectedProperties,
  ProposalAuthContinuationAbandonedProperties,
  ProposalAuthContinuationFailedProperties,
  ProposalAuthContinuationRestoredProperties,
  ProposalBackedProperties,
  ProposalCreatedProperties,
  ProposalCreateLoginRequestedProperties,
  ProposalDetailViewedProperties,
  ProposalFilterChangedProperties,
  ProposalFormStartedProperties,
  ProposalFormStepCompletedProperties,
  ProposalSearchProperties,
  ProposalWatchlistToggledProperties,
  ProposalWithdrawnProperties,
  SuggestOutcomeModalOpenedProperties,
  UniqueMarketAffirmationShownProperties,
} from './proposal-events';
import {
  CREATOR_REWARDS_PANEL_VIEWED,
  PROPOSAL_ADMIN_APPROVED,
  PROPOSAL_ADMIN_REJECTED,
  PROPOSAL_AUTH_CONTINUATION_ABANDONED,
  PROPOSAL_AUTH_CONTINUATION_FAILED,
  PROPOSAL_AUTH_CONTINUATION_RESTORED,
  PROPOSAL_BACKED,
  PROPOSAL_CREATE_LOGIN_REQUESTED,
  PROPOSAL_CREATED,
  PROPOSAL_DETAIL_VIEWED,
  PROPOSAL_FILTER_CHANGED,
  PROPOSAL_FORM_STARTED,
  PROPOSAL_FORM_STEP_COMPLETED,
  PROPOSAL_SEARCH,
  PROPOSAL_WATCHLIST_TOGGLED,
  PROPOSAL_WITHDRAWN,
  SUGGEST_OUTCOME_MODAL_OPENED,
} from './proposal-events';
import type { ResolutionCardSharedProperties } from './resolution-card-shared';
import { RESOLUTION_CARD_SHARED } from './resolution-card-shared';
import type {
  RewardsAccountCreatedProperties,
  RewardsContentKitOpenedProperties,
  RewardsContentKitUsedProperties,
  RewardsContentSubmittedProperties,
  RewardsEmailCaptureOpenedProperties,
  RewardsEmailVerifiedProperties,
  RewardsEnrollStuckProperties,
  RewardsEntryClickedProperties,
  RewardsGateBounceProperties,
  RewardsMilestoneClaimedProperties,
  RewardsPageViewedProperties,
  RewardsParticipantCreatedProperties,
  RewardsQuestStartedProperties,
  RewardsQuestVerifiedProperties,
  RewardsQuestVerifyFailedProperties,
  RewardsStartQuestingClickedProperties,
  RewardsStreakClaimedProperties,
  RewardsXConnectFailedProperties,
  RewardsXConnectStartedProperties,
  RewardsXConnectSucceededProperties,
} from './rewards';
import {
  REWARDS_ACCOUNT_CREATED,
  REWARDS_CONTENT_KIT_OPENED,
  REWARDS_CONTENT_KIT_USED,
  REWARDS_CONTENT_SUBMITTED,
  REWARDS_EMAIL_CAPTURE_OPENED,
  REWARDS_EMAIL_VERIFIED,
  REWARDS_ENROLL_STUCK,
  REWARDS_ENTRY_CLICKED,
  REWARDS_GATE_BOUNCE,
  REWARDS_MILESTONE_CLAIMED,
  REWARDS_PAGE_VIEWED,
  REWARDS_PARTICIPANT_CREATED,
  REWARDS_QUEST_STARTED,
  REWARDS_QUEST_VERIFIED,
  REWARDS_QUEST_VERIFY_FAILED,
  REWARDS_START_QUESTING_CLICKED,
  REWARDS_STREAK_CLAIMED,
  REWARDS_X_CONNECT_FAILED,
  REWARDS_X_CONNECT_STARTED,
  REWARDS_X_CONNECT_SUCCEEDED,
} from './rewards';
import type { ShareClickedProperties } from './share-clicked';
import { SHARE_CLICKED } from './share-clicked';
import type {
  SocialBoostSharedProperties,
  SocialCreatorClickedProperties,
  SocialPageLoadedProperties,
  SocialPodiumCardClickedProperties,
  SocialProposalBackSharedProperties,
  SocialProposalSharedProperties,
  SocialReferralLinkCopiedProperties,
  SocialReferralRankBackersSharedProperties,
  SocialReferralRankBoostersSharedProperties,
  SocialReferralRankCreatorsSharedProperties,
  SocialReferralRankSharedProperties,
  SocialTabSwitchedProperties,
  SocialThesisReactedProperties,
  SocialThesisRepliedProperties,
  SocialThesisReplyGateShownProperties,
  SocialThesisSharedProperties,
  SocialThesisTradeClickedProperties,
  SocialTradeSharedProperties,
} from './social';
import {
  SOCIAL_BOOST_SHARED,
  SOCIAL_CREATOR_CLICKED,
  SOCIAL_PAGE_LOADED,
  SOCIAL_PODIUM_CARD_CLICKED,
  SOCIAL_PROPOSAL_BACK_SHARED,
  SOCIAL_PROPOSAL_SHARED,
  SOCIAL_REFERRAL_LINK_COPIED,
  SOCIAL_REFERRAL_RANK_BACKERS_SHARED,
  SOCIAL_REFERRAL_RANK_BOOSTERS_SHARED,
  SOCIAL_REFERRAL_RANK_CREATORS_SHARED,
  SOCIAL_REFERRAL_RANK_SHARED,
  SOCIAL_TAB_SWITCHED,
  SOCIAL_THESIS_REACTED,
  SOCIAL_THESIS_REPLIED,
  SOCIAL_THESIS_REPLY_GATE_SHOWN,
  SOCIAL_THESIS_SHARED,
  SOCIAL_THESIS_TRADE_CLICKED,
  SOCIAL_TRADE_SHARED,
} from './social';
import type {
  LandingProductCtaClickedProperties,
  SupportMainnetDonateAddressCopiedProperties,
  SupportMainnetDonateQrAmountChangedProperties,
  SupportMainnetDonateQrViewedProperties,
  SupportMainnetFaqOpenedProperties,
  SupportMainnetPageViewedProperties,
  SupportMainnetShareClickedProperties,
  WaitlistSignupCompleteProperties,
} from './support-mainnet';
import {
  LANDING_PRODUCT_CTA_CLICKED,
  SUPPORT_MAINNET_DONATE_ADDRESS_COPIED,
  SUPPORT_MAINNET_DONATE_QR_AMOUNT_CHANGED,
  SUPPORT_MAINNET_DONATE_QR_VIEWED,
  SUPPORT_MAINNET_FAQ_OPENED,
  SUPPORT_MAINNET_PAGE_VIEWED,
  SUPPORT_MAINNET_SHARE_CLICKED,
  WAITLIST_SIGNUP_COMPLETE,
} from './support-mainnet';
import type {
  TradeAttemptedProperties,
  TradeErrorProperties,
  TradeSuccessProperties,
  TxBlockhashExpiredProperties,
  TxCancelledProperties,
  TxConfirmFailedProperties,
  TxConfirmSuccessProperties,
  TxEstimateHighPriceImpactProperties,
  TxEstimatePriceChangedProperties,
  TxMaxRetriesProperties,
  TxPriorityFeeFallbackProperties,
  TxPriorityFeeFetchedProperties,
  TxRelayAcceptedProperties,
  TxRelayFailedProperties,
  TxSignatureObservedProperties,
  TxSignFailedProperties,
  TxSignStartedProperties,
  TxSimFailedProperties,
  TxSimTimeoutProperties,
  TxStartedProperties,
  TxSubmitAttemptedProperties,
  TxTurnkeyFailoverTriggeredProperties,
  TxTurnkeyPollErrorProperties,
  TxTurnkeySendCompletedProperties,
} from './trading';
import {
  TRADE_ATTEMPTED,
  TRADE_ERROR,
  TRADE_SUCCESS,
  TRANSACTION_CONFIRMED,
  TRANSACTION_FAILED,
  TRANSACTION_SUBMITTED,
  TX_BLOCKHASH_EXPIRED,
  TX_CANCELLED,
  TX_CONFIRM_FAILED,
  TX_CONFIRM_SUCCESS,
  TX_ESTIMATE_HIGH_PRICE_IMPACT,
  TX_ESTIMATE_PRICE_CHANGED,
  TX_MAX_RETRIES,
  TX_PRIORITY_FEE_FALLBACK,
  TX_PRIORITY_FEE_FETCHED,
  TX_RELAY_ACCEPTED,
  TX_RELAY_FAILED,
  TX_SIGN_FAILED,
  TX_SIGN_STARTED,
  TX_SIGNATURE_OBSERVED,
  TX_SIM_FAILED,
  TX_SIM_TIMEOUT,
  TX_STARTED,
  TX_SUBMIT_ATTEMPTED,
  TX_TURNKEY_FAILOVER_TRIGGERED,
  TX_TURNKEY_POLL_ERROR,
  TX_TURNKEY_SEND_COMPLETED,
} from './trading';
import type { ViralPnlAutoPromptedProperties } from './viral-pnl-auto-prompted';
import { VIRAL_PNL_AUTO_PROMPTED } from './viral-pnl-auto-prompted';

// Re-export all event definitions for consumers.
export {
  PAGE_VIEWED,
  AUTH_METHOD_USED,
  AUTH_ERROR,
  AUTH_CALLBACK_HANG,
  LOGOUT,
  LOGOUT_ERROR,
  SESSION_RESTORED,
  SESSION_RESTORE_ERROR,
  SESSION_REFRESH,
  TURNKEY_SESSION_EXPIRED,
  TURNKEY_SESSION_REFRESHED,
  TURNKEY_REFRESH_FAILED,
  TURNKEY_SESSION_RECONCILE_LOGOUT,
  TURNKEY_POPUP_CALLBACK_SKIP,
  WALLET_EXPORT,
  LOCALE_CHANGED,
  LOCATION_CHANGED,
  CURRENCY_CHANGED,
  WALLET_CREATED,
  WALLET_CONNECTED,
  WALLET_RENAMED,
  WALLET_UNLINKED,
  WALLET_SWITCHED,
  PROFILE_UPDATED,
  PROFILE_VIEWED,
  ACCOUNT_DELETED,
  // Trading events (T060)
  TRADE_ATTEMPTED,
  TRADE_SUCCESS,
  TRADE_ERROR,
  TX_STARTED,
  TX_SIM_FAILED,
  TX_SIM_TIMEOUT,
  TX_PRIORITY_FEE_FETCHED,
  TX_PRIORITY_FEE_FALLBACK,
  TX_SIGN_STARTED,
  TX_SIGN_FAILED,
  TX_RELAY_ACCEPTED,
  TX_RELAY_FAILED,
  TX_CONFIRM_SUCCESS,
  TX_CONFIRM_FAILED,
  TX_BLOCKHASH_EXPIRED,
  TX_MAX_RETRIES,
  TX_CANCELLED,
  TX_SUBMIT_ATTEMPTED,
  TX_TURNKEY_SEND_COMPLETED,
  TX_TURNKEY_FAILOVER_TRIGGERED,
  TX_SIGNATURE_OBSERVED,
  TX_TURNKEY_POLL_ERROR,
  // Estimate analytics events (T121)
  TX_ESTIMATE_PRICE_CHANGED,
  TX_ESTIMATE_HIGH_PRICE_IMPACT,
  // RUM action names
  TRANSACTION_SUBMITTED,
  TRANSACTION_CONFIRMED,
  TRANSACTION_FAILED,
  // Markets page events
  MARKETS_PAGE_VIEWED,
  MARKETS_SORT_CHANGED,
  MARKETS_FILTER_CHANGED,
  MARKETS_CARD_CLICKED,
  MARKETS_BOOKMARK_TOGGLED,
  MARKETS_LOAD_MORE,
  MARKETS_SEARCH_QUERIED,
  // Market detail page events (T047–T053)
  MARKET_DETAIL_VIEWED,
  // Close-to-resolving banner (spec 102-close-to-resolving-banner)
  MARKET_APPROACHING_RESOLUTION_VIEWED,
  // Market lifecycle modals (spec 112-market-lifecycle-modals)
  MARKET_LIFECYCLE_MODAL_SHOWN,
  // Market resolution hero events (spec 101-market-resolution-hero)
  MARKET_RESULT_VIEWED,
  MARKET_RESULT_SHARED,
  // Breaking views widget event (063-breaking-views)
  BREAKING_VIEW_TAPPED,
  OUTCOME_SELECTED,
  BOOST_INITIATED,
  BOOST_COMPLETED,
  CLAIM_INITIATED,
  SHARE_CLICKED,
  PNL_CARD_SHARED,
  VIRAL_PNL_AUTO_PROMPTED,
  PNL_MARKET_CARD_SHARED,
  PRIZE_CLAIM_SHARED,
  RESOLUTION_CARD_SHARED,
  BOOKMARK_TOGGLED,
  // Proposal events (T025)
  PROPOSAL_FORM_STARTED,
  PROPOSAL_FORM_STEP_COMPLETED,
  PROPOSAL_CREATED,
  PROPOSAL_BACKED,
  PROPOSAL_WITHDRAWN,
  PROPOSAL_SEARCH,
  PROPOSAL_WATCHLIST_TOGGLED,
  PROPOSAL_FILTER_CHANGED,
  PROPOSAL_DETAIL_VIEWED,
  PROPOSAL_ADMIN_APPROVED,
  PROPOSAL_ADMIN_REJECTED,
  PROPOSAL_AUTH_CONTINUATION_ABANDONED,
  PROPOSAL_AUTH_CONTINUATION_FAILED,
  PROPOSAL_AUTH_CONTINUATION_RESTORED,
  PROPOSAL_CREATE_LOGIN_REQUESTED,
  // Suggest-outcome modal — launchpad detail (spec 078)
  SUGGEST_OUTCOME_MODAL_OPENED,
  // Creator rewards panel — launchpad detail (spec 079)
  CREATOR_REWARDS_PANEL_VIEWED,
  // Onboarding events (013-new-user-onboarding)
  ONBOARDING_STARTED,
  ONBOARDING_STAGE_VIEWED,
  ONBOARDING_STAGE_COMPLETED,
  ONBOARDING_COMPLETED,
  ONBOARDING_EXITED,
  // Social events (016-social-page)
  SOCIAL_TAB_SWITCHED,
  SOCIAL_THESIS_REACTED,
  SOCIAL_THESIS_REPLY_GATE_SHOWN,
  SOCIAL_THESIS_REPLIED,
  SOCIAL_THESIS_SHARED,
  SOCIAL_THESIS_TRADE_CLICKED,
  SOCIAL_PROPOSAL_SHARED,
  SOCIAL_PROPOSAL_BACK_SHARED,
  SOCIAL_TRADE_SHARED,
  SOCIAL_BOOST_SHARED,
  SOCIAL_REFERRAL_LINK_COPIED,
  SOCIAL_REFERRAL_RANK_SHARED,
  SOCIAL_REFERRAL_RANK_BOOSTERS_SHARED,
  SOCIAL_REFERRAL_RANK_CREATORS_SHARED,
  SOCIAL_REFERRAL_RANK_BACKERS_SHARED,
  SOCIAL_CREATOR_CLICKED,
  SOCIAL_PAGE_LOADED,
  // Social podium events (071-social-podium-cards)
  SOCIAL_PODIUM_CARD_CLICKED,
  // Gift funnel events (006-handle-gifting, FR-077)
  GIFT_MODAL_OPENED,
  GIFT_MODAL_DISMISSED,
  GIFT_SEND_INITIATED,
  GIFT_SEND_COMPLETED,
  GIFT_SHARE_CLICKED,
  GIFT_SHARE_CLICKED_X,
  GIFT_SHARE_CLICKED_COPY_LINK,
  GIFT_PERMALINK_VIEWED,
  GIFT_CLAIM_INITIATED,
  GIFT_CLAIM_COMPLETED,
  GIFT_WITHDRAW_INITIATED,
  GIFT_WITHDRAW_COMPLETED,
  GIFT_ONBOARDING_COMPLETED_VIA_CLAIM,
  GIFT_ASSET_SELECTED,
  GIFT_OUTCOME_TOKEN_PICKED,
  // Support mainnet events (030-mainnet-support-page)
  LANDING_PRODUCT_CTA_CLICKED,
  SUPPORT_MAINNET_PAGE_VIEWED,
  SUPPORT_MAINNET_DONATE_QR_VIEWED,
  SUPPORT_MAINNET_DONATE_ADDRESS_COPIED,
  SUPPORT_MAINNET_DONATE_QR_AMOUNT_CHANGED,
  SUPPORT_MAINNET_SHARE_CLICKED,
  SUPPORT_MAINNET_FAQ_OPENED,
  WAITLIST_SIGNUP_COMPLETE,
  // Engineer impersonation events (048-impersonate-user)
  ENGINEER_IMPERSONATION_STARTED,
  ENGINEER_IMPERSONATION_STOPPED,
  // Early-access waitlist events
  EARLY_ACCESS_WAITLIST_OPENED,
  WAITLIST_OTP_SENT,
  WAITLIST_OTP_VERIFIED,
  WAITLIST_OTP_FAILED,
  WAITLIST_X_STARTED,
  WAITLIST_X_JOINED,
  // Faucet events (080-portfolio-faucet)
  FAUCET_CLAIM_INITIATED,
  FAUCET_CLAIM_SUCCEEDED,
  FAUCET_CLAIM_FAILED,
  // Feedback & Reports events
  FEEDBACK_OPENED,
  FEEDBACK_SUBMITTED,
  APP_UPSELL_VIEWED,
  APP_UPSELL_CLICKED,
  APP_UPSELL_DISMISSED,
  // Featured market hero events
  FEATURED_HERO_VIEWED,
  FEATURED_HERO_CARD_CLICKED,
  FEATURED_HERO_THESIS_CLICKED,
  FEATURED_HERO_CAROUSEL_ADVANCED,
  FEATURED_HERO_SHARE_OPENED,
  FEATURED_HERO_BOOKMARK_TOGGLED,
  // AI image generation events
  IMAGE_GENERATION_STARTED,
  IMAGE_GENERATION_COMPLETED,
  IMAGE_GENERATION_FAILED,
  IMAGE_GENERATION_USED,
  IMAGE_GENERATION_RETRIED,
  // Image search events (launchpad image picker — SerpAPI / Brave)
  IMAGE_SEARCH_STARTED,
  IMAGE_SEARCH_COMPLETED,
  IMAGE_SEARCH_FAILED,
  IMAGE_SEARCH_USED,
  // Profile portfolio card events (spec 081)
  PROFILE_PORTFOLIO_CARD_SHARE_CLICKED,
  PROFILE_PORTFOLIO_CARD_KPI_CLICKED,
  // Portfolio positions panel events (spec 083)
  PORTFOLIO_POSITIONS_TOGGLED,
  PORTFOLIO_POSITION_SELL_CLICKED,
  // Rewards launch telemetry (spec 004 — docs/rewards-launch-telemetry-plan.md §6.2)
  REWARDS_ENTRY_CLICKED,
  REWARDS_START_QUESTING_CLICKED,
  REWARDS_PAGE_VIEWED,
  REWARDS_GATE_BOUNCE,
  REWARDS_X_CONNECT_STARTED,
  REWARDS_X_CONNECT_SUCCEEDED,
  REWARDS_X_CONNECT_FAILED,
  REWARDS_EMAIL_CAPTURE_OPENED,
  REWARDS_EMAIL_VERIFIED,
  REWARDS_PARTICIPANT_CREATED,
  REWARDS_ACCOUNT_CREATED,
  REWARDS_QUEST_STARTED,
  REWARDS_QUEST_VERIFIED,
  REWARDS_QUEST_VERIFY_FAILED,
  REWARDS_STREAK_CLAIMED,
  REWARDS_MILESTONE_CLAIMED,
  REWARDS_CONTENT_SUBMITTED,
  REWARDS_CONTENT_KIT_OPENED,
  REWARDS_CONTENT_KIT_USED,
  REWARDS_ENROLL_STUCK,
};
export type {
  AccountDeletedProperties,
  AuthCallbackHangProperties,
  AuthErrorProperties,
  AuthMethod,
  AuthMethodUsedProperties,
  CurrencyChangedProperties,
  LocaleChangedProperties,
  LocationChangedProperties,
  LogoutErrorProperties,
  LogoutProperties,
  ProfileUpdatedProperties,
  ProfileViewedProperties,
  SessionRefreshProperties,
  SessionRestoredProperties,
  SessionRestoreErrorProperties,
  TurnkeyPopupCallbackSkipProperties,
  TurnkeyRefreshFailedProperties,
  TurnkeySessionExpiredProperties,
  TurnkeySessionReconcileLogoutProperties,
  TurnkeySessionRefreshedProperties,
  WalletConnectedProperties,
  WalletCreatedProperties,
  WalletExportProperties,
  WalletReconcileSkippedForeignSubOrgProperties,
  WalletRenamedProperties,
  WalletSwitchedProperties,
  WalletUnlinkedProperties,
  WalletUnreachableSubOrgProperties,
} from './auth';
export type { BookmarkToggledProperties } from './bookmark-toggled';
export type { BoostCompletedProperties } from './boost-completed';
export type { BoostInitiatedProperties } from './boost-initiated';
export type { ClaimInitiatedProperties } from './claim-initiated';
export type { EarlyAccessWaitlistOpenedProperties } from './early-access';
export type {
  EngineerImpersonationStartedProperties,
  EngineerImpersonationStoppedProperties,
} from './engineer-impersonation';
export type {
  FaucetClaimFailedProperties,
  FaucetClaimFailureReason,
  FaucetClaimInitiatedProperties,
  FaucetClaimSource,
  FaucetClaimSucceededProperties,
} from './faucet';
export type {
  FeaturedHeroBookmarkToggledProperties,
  FeaturedHeroCardClickedProperties,
  FeaturedHeroCarouselAdvancedProperties,
  FeaturedHeroCarouselTrigger,
  FeaturedHeroShareOpenedProperties,
  FeaturedHeroSource,
  FeaturedHeroThesisClickedProperties,
  FeaturedHeroViewedProperties,
} from './featured-hero';
export type { FeedbackOpenedProperties, FeedbackSubmittedProperties } from './feedback';
export type {
  GiftAssetSelectedProperties,
  GiftAssetType,
  GiftClaimCompletedProperties,
  GiftClaimInitiatedProperties,
  GiftModalDismissedProperties,
  GiftModalOpenedProperties,
  GiftOnboardingCompletedViaClaimProperties,
  GiftOutcomeTokenPickedProperties,
  GiftPermalinkViewedProperties,
  GiftSendCompletedProperties,
  GiftSendInitiatedProperties,
  GiftShareChannel,
  GiftShareClickedCopyLinkProperties,
  GiftShareClickedProperties,
  GiftShareClickedXProperties,
  GiftWithdrawCompletedProperties,
  GiftWithdrawInitiatedProperties,
} from './gifts';
export type {
  ImageGenerationCompletedProperties,
  ImageGenerationFailedProperties,
  ImageGenerationFailureReason,
  ImageGenerationMode,
  ImageGenerationRetriedProperties,
  ImageGenerationStartedProperties,
  ImageGenerationUsedProperties,
  ImageSearchCompletedProperties,
  ImageSearchFailedProperties,
  ImageSearchFailureReason,
  ImageSearchProvider,
  ImageSearchStartedProperties,
  ImageSearchUsedProperties,
} from './image-generation';
export type { MarketDetailViewedProperties } from './market-detail-viewed';
export type { MarketLifecycleModalShownProperties } from './market-lifecycle';
export type { MarketResultSharedProperties } from './market-result-shared';
export type {
  MarketResultViewedProperties,
  MarketResultViewedStage,
  MarketResultViewedUserOutcome,
} from './market-result-viewed';
export type {
  MarketsBookmarkToggledProperties,
  MarketsCardClickedProperties,
  MarketsFilterChangedProperties,
  MarketsLoadMoreProperties,
  MarketsPageViewedProperties,
  MarketsSortChangedProperties,
} from './markets';
export type { BreakingViewTappedProperties } from './markets-feed';
export type { EventDefinition, PageViewedProperties } from './navigation';
export type {
  OnboardingCompletedProperties,
  OnboardingExitedProperties,
  OnboardingStageCompletedProperties,
  OnboardingStageName,
  OnboardingStageViewedProperties,
  OnboardingStartedProperties,
} from './onboarding';
export type { OutcomeSelectedProperties } from './outcome-selected';
export type { PnlCardSharedProperties } from './pnl-card-shared';
export type { PnlMarketCardSharedProperties } from './pnl-market-card-shared';
export type {
  PortfolioPositionSellClickedProperties,
  PortfolioPositionsToggledProperties,
} from './portfolio';
export type { PrizeClaimSharedProperties } from './prize-claim-shared';
export type {
  ProfilePortfolioCardKpi,
  ProfilePortfolioCardKpiClickedProperties,
  ProfilePortfolioCardShareClickedProperties,
  ProfilePortfolioCardViewerMode,
} from './profile-portfolio-card';
export type {
  CreatorRewardsPanelViewedProperties,
  DuplicateNudgeDismissedProperties,
  DuplicateNudgeShownProperties,
  ProposalAdminApprovedProperties,
  ProposalAdminRejectedProperties,
  ProposalAuthContinuationAbandonedProperties,
  ProposalAuthContinuationFailedProperties,
  ProposalAuthContinuationRestoredProperties,
  ProposalBackedProperties,
  ProposalCreatedProperties,
  ProposalCreateLoginRequestedProperties,
  ProposalDetailViewedProperties,
  ProposalFilterChangedProperties,
  ProposalFormStartedProperties,
  ProposalFormStepCompletedProperties,
  ProposalSearchProperties,
  ProposalWatchlistToggledProperties,
  ProposalWithdrawnProperties,
  SuggestOutcomeModalOpenedProperties,
  UniqueMarketAffirmationShownProperties,
} from './proposal-events';
export type { ResolutionCardSharedProperties } from './resolution-card-shared';
export type {
  RewardsAccountCreatedProperties,
  RewardsContentKitOpenedProperties,
  RewardsContentKitUsedProperties,
  RewardsContentSubmittedProperties,
  RewardsEmailCaptureOpenedProperties,
  RewardsEmailVerifiedProperties,
  RewardsEnrollStuckProperties,
  RewardsEntryClickedProperties,
  RewardsGateBounceProperties,
  RewardsMilestoneClaimedProperties,
  RewardsPageViewedProperties,
  RewardsParticipantCreatedProperties,
  RewardsQuestStartedProperties,
  RewardsQuestVerifiedProperties,
  RewardsQuestVerifyFailedProperties,
  RewardsStartQuestingClickedProperties,
  RewardsStreakClaimedProperties,
  RewardsXConnectFailedProperties,
  RewardsXConnectStartedProperties,
  RewardsXConnectSucceededProperties,
} from './rewards';
export type { ShareClickedProperties } from './share-clicked';
export type {
  SocialBoostSharedProperties,
  SocialCreatorClickedProperties,
  SocialPageLoadedProperties,
  SocialPodiumCardClickedProperties,
  SocialProposalBackSharedProperties,
  SocialProposalSharedProperties,
  SocialReferralLinkCopiedProperties,
  SocialReferralRankBackersSharedProperties,
  SocialReferralRankBoostersSharedProperties,
  SocialReferralRankCreatorsSharedProperties,
  SocialReferralRankSharedProperties,
  SocialTabSwitchedProperties,
  SocialThesisReactedProperties,
  SocialThesisRepliedProperties,
  SocialThesisReplyGateShownProperties,
  SocialThesisSharedProperties,
  SocialThesisTradeClickedProperties,
  SocialTradeSharedProperties,
} from './social';
export type {
  LandingProductCtaClickedProperties,
  SupportMainnetDonateAddressCopiedProperties,
  SupportMainnetDonateQrAmountChangedProperties,
  SupportMainnetDonateQrViewedProperties,
  SupportMainnetFaqOpenedProperties,
  SupportMainnetPageViewedProperties,
  SupportMainnetShareChannel,
  SupportMainnetShareClickedProperties,
  WaitlistSignupCompleteProperties,
} from './support-mainnet';
export type {
  TradeAttemptedProperties,
  TradeErrorProperties,
  TradeSuccessProperties,
  TxBlockhashExpiredProperties,
  TxCancelledProperties,
  TxConfirmFailedProperties,
  TxConfirmSuccessProperties,
  TxEstimateHighPriceImpactProperties,
  TxEstimatePriceChangedProperties,
  TxMaxRetriesProperties,
  TxPriorityFeeFallbackProperties,
  TxPriorityFeeFetchedProperties,
  TxRelayAcceptedProperties,
  TxRelayFailedProperties,
  TxSignFailedProperties,
  TxSignStartedProperties,
  TxSimFailedProperties,
  TxSimTimeoutProperties,
  TxStartedProperties,
  TxTurnkeyPollErrorProperties,
} from './trading';
export type { ViralPnlAutoPromptedProperties } from './viral-pnl-auto-prompted';

// ---------------------------------------------------------------------------
// Event registry — maps event name -> property type.
// Add new entries here as new event definitions are created.
// ---------------------------------------------------------------------------
export interface EventRegistry {
  page_viewed: PageViewedProperties;
  auth_method_used: AuthMethodUsedProperties;
  auth_error: AuthErrorProperties;
  auth_callback_hang: AuthCallbackHangProperties;
  logout: LogoutProperties;
  logout_error: LogoutErrorProperties;
  session_restored: SessionRestoredProperties;
  session_restore_error: SessionRestoreErrorProperties;
  turnkey_session_expired: TurnkeySessionExpiredProperties;
  turnkey_session_refreshed: TurnkeySessionRefreshedProperties;
  turnkey_refresh_failed: TurnkeyRefreshFailedProperties;
  turnkey_session_reconcile_logout: TurnkeySessionReconcileLogoutProperties;
  turnkey_popup_callback_skip: TurnkeyPopupCallbackSkipProperties;
  session_refresh: SessionRefreshProperties;
  wallet_export: WalletExportProperties;
  wallet_unreachable_sub_org: WalletUnreachableSubOrgProperties;
  wallet_reconcile_skipped_foreign_sub_org: WalletReconcileSkippedForeignSubOrgProperties;
  wallet_created: WalletCreatedProperties;
  wallet_connected: WalletConnectedProperties;
  wallet_renamed: WalletRenamedProperties;
  wallet_unlinked: WalletUnlinkedProperties;
  wallet_switched: WalletSwitchedProperties;
  profile_updated: ProfileUpdatedProperties;
  profile_viewed: ProfileViewedProperties;
  account_deleted: AccountDeletedProperties;
  locale_changed: LocaleChangedProperties;
  location_changed: LocationChangedProperties;
  currency_changed: CurrencyChangedProperties;
  // Trade-level summary events
  trade_attempted: TradeAttemptedProperties;
  trade_success: TradeSuccessProperties;
  boost_success: TradeSuccessProperties;
  claim_prize_success: TradeSuccessProperties;
  claim_backer_fees_success: TradeSuccessProperties;
  trade_error: TradeErrorProperties;
  trade_unknown: TradeErrorProperties;
  trade_cancelled: TradeAttemptedProperties & { errorCode: string };
  // Trading events (T060)
  tx_started: TxStartedProperties;
  tx_simulation_failed: TxSimFailedProperties;
  tx_simulation_timeout: TxSimTimeoutProperties;
  tx_priority_fee_fetched: TxPriorityFeeFetchedProperties;
  tx_priority_fee_fallback: TxPriorityFeeFallbackProperties;
  tx_sign_started: TxSignStartedProperties;
  tx_sign_failed: TxSignFailedProperties;
  tx_relay_accepted: TxRelayAcceptedProperties;
  tx_relay_failed: TxRelayFailedProperties;
  tx_confirm_success: TxConfirmSuccessProperties;
  tx_confirm_failed: TxConfirmFailedProperties;
  tx_blockhash_expired: TxBlockhashExpiredProperties;
  tx_max_retries: TxMaxRetriesProperties;
  tx_cancelled: TxCancelledProperties;
  // Spec 056 — Turnkey routing telemetry
  tx_submit_attempted: TxSubmitAttemptedProperties;
  tx_turnkey_send_completed: TxTurnkeySendCompletedProperties;
  tx_turnkey_failover_triggered: TxTurnkeyFailoverTriggeredProperties;
  tx_signature_observed: TxSignatureObservedProperties;
  tx_turnkey_poll_error: TxTurnkeyPollErrorProperties;
  // Estimate analytics events (T121)
  tx_estimate_price_changed: TxEstimatePriceChangedProperties;
  tx_estimate_high_price_impact: TxEstimateHighPriceImpactProperties;
  // Markets page events
  markets_page_viewed: MarketsPageViewedProperties;
  markets_sort_changed: MarketsSortChangedProperties;
  markets_filter_changed: MarketsFilterChangedProperties;
  markets_card_clicked: MarketsCardClickedProperties;
  markets_bookmark_toggled: MarketsBookmarkToggledProperties;
  markets_load_more: MarketsLoadMoreProperties;
  markets_search_queried: MarketsSearchQueriedProperties;
  markets_watchlist_toggled: { active: boolean };
  // Market detail page events (T047–T053)
  market_detail_viewed: MarketDetailViewedProperties;
  // Close-to-resolving banner (spec 102-close-to-resolving-banner)
  market_approaching_resolution_viewed: MarketApproachingResolutionViewedProperties;
  // Market lifecycle modals (spec 112-market-lifecycle-modals)
  market_lifecycle_modal_shown: MarketLifecycleModalShownProperties;
  // Market resolution hero events (spec 101-market-resolution-hero)
  market_result_viewed: MarketResultViewedProperties;
  market_result_shared: MarketResultSharedProperties;
  // Breaking views widget event (063-breaking-views)
  breaking_view_tapped: BreakingViewTappedProperties;
  outcome_selected: OutcomeSelectedProperties;
  boost_initiated: BoostInitiatedProperties;
  boost_completed: BoostCompletedProperties;
  claim_initiated: ClaimInitiatedProperties;
  claim_confirmed: ClaimConfirmedProperties;
  share_clicked: ShareClickedProperties;
  pnl_card_shared: PnlCardSharedProperties;
  viral_pnl_auto_prompted: ViralPnlAutoPromptedProperties;
  pnl_market_card_shared: PnlMarketCardSharedProperties;
  prize_claim_shared: PrizeClaimSharedProperties;
  // Resolution result card share (MOR-227)
  resolution_card_shared: ResolutionCardSharedProperties;
  bookmark_toggled: BookmarkToggledProperties;
  // Similar-markets nudge (spec 048-similar-markets §6)
  duplicate_nudge_shown: DuplicateNudgeShownProperties;
  duplicate_nudge_dismissed: DuplicateNudgeDismissedProperties;
  unique_market_affirmation_shown: UniqueMarketAffirmationShownProperties;
  // Proposal events (T025)
  proposal_form_started: ProposalFormStartedProperties;
  proposal_form_step_completed: ProposalFormStepCompletedProperties;
  proposal_created: ProposalCreatedProperties;
  proposal_create_login_requested: ProposalCreateLoginRequestedProperties;
  proposal_auth_continuation_restored: ProposalAuthContinuationRestoredProperties;
  proposal_auth_continuation_failed: ProposalAuthContinuationFailedProperties;
  proposal_auth_continuation_abandoned: ProposalAuthContinuationAbandonedProperties;
  proposal_backed: ProposalBackedProperties;
  proposal_withdrawn: ProposalWithdrawnProperties;
  proposal_search: ProposalSearchProperties;
  proposal_filter_changed: ProposalFilterChangedProperties;
  proposal_detail_viewed: ProposalDetailViewedProperties;
  proposal_admin_approved: ProposalAdminApprovedProperties;
  proposal_admin_rejected: ProposalAdminRejectedProperties;
  proposal_watchlist_toggled: ProposalWatchlistToggledProperties;
  // Suggest-outcome modal — launchpad detail (spec 078)
  suggest_outcome_modal_opened: SuggestOutcomeModalOpenedProperties;
  // Creator rewards panel — launchpad detail (spec 079)
  creator_rewards_panel_viewed: CreatorRewardsPanelViewedProperties;
  // Onboarding events (013-new-user-onboarding)
  onboarding_started: OnboardingStartedProperties;
  onboarding_stage_viewed: OnboardingStageViewedProperties;
  onboarding_stage_completed: OnboardingStageCompletedProperties;
  onboarding_completed: OnboardingCompletedProperties;
  onboarding_exited: OnboardingExitedProperties;
  // Social events (016-social-page)
  social_tab_switched: SocialTabSwitchedProperties;
  social_thesis_reacted: SocialThesisReactedProperties;
  social_thesis_reply_gate_shown: SocialThesisReplyGateShownProperties;
  social_thesis_replied: SocialThesisRepliedProperties;
  social_thesis_shared: SocialThesisSharedProperties;
  social_proposal_shared: SocialProposalSharedProperties;
  social_thesis_trade_clicked: SocialThesisTradeClickedProperties;
  social_trade_shared: SocialTradeSharedProperties;
  social_boost_shared: SocialBoostSharedProperties;
  social_proposal_back_shared: SocialProposalBackSharedProperties;
  social_referral_link_copied: SocialReferralLinkCopiedProperties;
  social_referral_rank_shared: SocialReferralRankSharedProperties;
  social_referral_rank_boosters_shared: SocialReferralRankBoostersSharedProperties;
  social_referral_rank_creators_shared: SocialReferralRankCreatorsSharedProperties;
  social_referral_rank_backers_shared: SocialReferralRankBackersSharedProperties;
  social_creator_clicked: SocialCreatorClickedProperties;
  social_page_loaded: SocialPageLoadedProperties;
  // Social podium events (071-social-podium-cards)
  social_podium_card_clicked: SocialPodiumCardClickedProperties;
  // Gift funnel events (006-handle-gifting, FR-077)
  gift_modal_opened: GiftModalOpenedProperties;
  gift_modal_dismissed: GiftModalDismissedProperties;
  gift_send_initiated: GiftSendInitiatedProperties;
  gift_send_completed: GiftSendCompletedProperties;
  gift_share_clicked: GiftShareClickedProperties;
  gift_share_clicked_x: GiftShareClickedXProperties;
  gift_share_clicked_copy_link: GiftShareClickedCopyLinkProperties;
  gift_permalink_viewed: GiftPermalinkViewedProperties;
  gift_claim_initiated: GiftClaimInitiatedProperties;
  gift_claim_completed: GiftClaimCompletedProperties;
  gift_withdraw_initiated: GiftWithdrawInitiatedProperties;
  gift_withdraw_completed: GiftWithdrawCompletedProperties;
  gift_onboarding_completed_via_claim: GiftOnboardingCompletedViaClaimProperties;
  gift_asset_selected: GiftAssetSelectedProperties;
  gift_outcome_token_picked: GiftOutcomeTokenPickedProperties;
  // Support mainnet events (030-mainnet-support-page)
  landing_product_cta_clicked: LandingProductCtaClickedProperties;
  support_mainnet_page_viewed: SupportMainnetPageViewedProperties;
  support_mainnet_donate_qr_viewed: SupportMainnetDonateQrViewedProperties;
  support_mainnet_donate_address_copied: SupportMainnetDonateAddressCopiedProperties;
  support_mainnet_donate_qr_amount_changed: SupportMainnetDonateQrAmountChangedProperties;
  support_mainnet_share_clicked: SupportMainnetShareClickedProperties;
  support_mainnet_faq_opened: SupportMainnetFaqOpenedProperties;
  waitlist_signup_complete: WaitlistSignupCompleteProperties;
  // Engineer impersonation events (048-impersonate-user)
  engineer_impersonation_started: EngineerImpersonationStartedProperties;
  engineer_impersonation_stopped: EngineerImpersonationStoppedProperties;
  // Early-access waitlist events
  early_access_waitlist_opened: EarlyAccessWaitlistOpenedProperties;
  waitlist_otp_sent: WaitlistOtpSentProperties;
  waitlist_otp_verified: WaitlistOtpVerifiedProperties;
  waitlist_otp_failed: WaitlistOtpFailedProperties;
  waitlist_x_started: WaitlistXStartedProperties;
  waitlist_x_joined: WaitlistXJoinedProperties;
  // Faucet events (080-portfolio-faucet)
  faucet_claim_initiated: FaucetClaimInitiatedProperties;
  faucet_claim_succeeded: FaucetClaimSucceededProperties;
  faucet_claim_failed: FaucetClaimFailedProperties;
  // Feedback & Reports events
  feedback_opened: FeedbackOpenedProperties;
  app_upsell_viewed: AppUpsellProperties;
  app_upsell_clicked: AppUpsellClickedProperties;
  app_upsell_dismissed: AppUpsellProperties;
  feedback_submitted: FeedbackSubmittedProperties;
  // Featured market hero events
  featured_hero_viewed: FeaturedHeroViewedProperties;
  featured_hero_card_clicked: FeaturedHeroCardClickedProperties;
  featured_hero_thesis_clicked: FeaturedHeroThesisClickedProperties;
  featured_hero_carousel_advanced: FeaturedHeroCarouselAdvancedProperties;
  featured_hero_share_opened: FeaturedHeroShareOpenedProperties;
  featured_hero_bookmark_toggled: FeaturedHeroBookmarkToggledProperties;
  // AI image generation events
  image_generation_started: ImageGenerationStartedProperties;
  image_generation_completed: ImageGenerationCompletedProperties;
  image_generation_failed: ImageGenerationFailedProperties;
  image_generation_used: ImageGenerationUsedProperties;
  image_generation_retried: ImageGenerationRetriedProperties;
  // Image search events (launchpad image picker — SerpAPI / Brave)
  image_search_started: ImageSearchStartedProperties;
  image_search_completed: ImageSearchCompletedProperties;
  image_search_failed: ImageSearchFailedProperties;
  image_search_used: ImageSearchUsedProperties;
  // Profile portfolio card events (spec 081)
  profile_portfolio_card_share_clicked: ProfilePortfolioCardShareClickedProperties;
  profile_portfolio_card_kpi_clicked: ProfilePortfolioCardKpiClickedProperties;
  // Portfolio positions panel events (spec 083)
  portfolio_positions_toggled: PortfolioPositionsToggledProperties;
  portfolio_position_sell_clicked: PortfolioPositionSellClickedProperties;
  // Rewards launch telemetry (spec 004 — docs/rewards-launch-telemetry-plan.md §6.2)
  rewards_entry_clicked: RewardsEntryClickedProperties;
  rewards_start_questing_clicked: RewardsStartQuestingClickedProperties;
  rewards_page_viewed: RewardsPageViewedProperties;
  rewards_gate_bounce: RewardsGateBounceProperties;
  rewards_x_connect_started: RewardsXConnectStartedProperties;
  rewards_x_connect_succeeded: RewardsXConnectSucceededProperties;
  rewards_email_capture_opened: RewardsEmailCaptureOpenedProperties;
  rewards_email_verified: RewardsEmailVerifiedProperties;
  rewards_x_connect_failed: RewardsXConnectFailedProperties;
  rewards_participant_created: RewardsParticipantCreatedProperties;
  rewards_account_created: RewardsAccountCreatedProperties;
  rewards_quest_started: RewardsQuestStartedProperties;
  rewards_quest_verified: RewardsQuestVerifiedProperties;
  rewards_quest_verify_failed: RewardsQuestVerifyFailedProperties;
  rewards_streak_claimed: RewardsStreakClaimedProperties;
  rewards_milestone_claimed: RewardsMilestoneClaimedProperties;
  rewards_content_submitted: RewardsContentSubmittedProperties;
  rewards_content_kit_opened: RewardsContentKitOpenedProperties;
  rewards_content_kit_used: RewardsContentKitUsedProperties;
  rewards_enroll_stuck: RewardsEnrollStuckProperties;
}

// ---------------------------------------------------------------------------
// Typed onEvent — accepts only registered event names and enforces the
// matching property shape at the call-site.
// ---------------------------------------------------------------------------

/**
 * Lifecycle events that MUST fire even while engineer impersonation is
 * active. Per spec 048 FR-038 / R-17: every other per-action event is
 * suppressed during impersonation so the impersonated user's analytics
 * profile is not polluted by engineer activity. The lifecycle pair
 * itself is the only attribution signal we keep so the engineer's
 * session boundary can still be reconstructed.
 */
const IMPERSONATION_LIFECYCLE_EVENTS: ReadonlySet<string> = new Set<string>([
  'engineer_impersonation_started',
  'engineer_impersonation_stopped',
]);

export function onEvent<K extends keyof EventRegistry>(
  name: K,
  properties: EventRegistry[K],
): void {
  // Spec 048 FR-038 / R-17: suppress per-action analytics during
  // impersonation, except for the lifecycle pair. Reading the store
  // via vanilla `getState()` works outside React and won't subscribe
  // anyone to re-renders.
  const isImpersonating: boolean = useImpersonationStore.getState().isActive;
  if (isImpersonating && !IMPERSONATION_LIFECYCLE_EVENTS.has(name)) {
    return;
  }

  // Defence in depth (FR-035..FR-037): scrub any 64-char hex grant
  // token that might have slipped into a payload before dispatching
  // to analytics providers.
  const scrubbedProperties: EventRegistry[K] = scrubGrantTokens(properties);
  trackEvent(name, scrubbedProperties);
  // Statsig events are not queryable as metrics; the allowlisted auth and
  // onboarding events are mirrored to an OTel counter so Better Stack can
  // show login success rate and the signup funnel next to the failures it
  // already charts. Same impersonation gate as analytics, by construction.
  reportFunnelEvent(name, scrubbedProperties);
}
